import request, { gql } from 'graphql-request';
import { SMART_MARGIN_FRAGMENT, ISOLATED_MARGIN_FRAGMENT, DEFAULT_NUMBER_OF_TRADES, } from '../constants/futures';
import { mapMarginTransfers, mapSmartMarginTransfers } from '../utils/futures';
import { getFuturesPositions, getFuturesTrades } from '../utils/subgraph';
import { UNSUPPORTED_NETWORK } from '../constants/errors';
import { toHex } from 'viem';
export const queryAccountsFromSubgraph = async (sdk, walletAddress) => {
    if (!walletAddress)
        return [];
    const response = await request(sdk.futures.futuresGqlEndpoint, gql `
			query crossMarginAccounts($owner: String!) {
				crossMarginAccounts(where: { owner: $owner }) {
					id
					owner
				}
			}
		`, { owner: walletAddress });
    return response?.crossMarginAccounts.map((cm) => cm.id) || [];
};
export const querySmartMarginAccounts = async (sdk, walletAddress) => {
    // TODO: Contract should be updating to support one to many
    const { SmartMarginAccountFactory } = sdk.context.defaultContractConfigs;
    if (!SmartMarginAccountFactory) {
        throw new Error(UNSUPPORTED_NETWORK);
    }
    const accounts = await sdk.context.client.readContract({
        ...SmartMarginAccountFactory,
        functionName: 'getAccountsOwnedBy',
        args: [walletAddress],
    });
    return accounts ?? [];
};
export const queryTrades = async (sdk, params) => {
    const filter = {
        accountType: 'smart_margin',
    };
    if (params.walletAddress) {
        filter['account'] = params.walletAddress;
    }
    if (params.marketAsset) {
        filter['asset'] = toHex(params.marketAsset, { size: 32 });
    }
    if (params.minTimestamp) {
        filter['timestamp_gt'] = params.minTimestamp.toString();
    }
    if (params.maxTimestamp) {
        filter['timestamp_lt'] = params.maxTimestamp.toString();
    }
    return getFuturesTrades(sdk.futures.futuresGqlEndpoint, {
        first: params.pageLength,
        where: filter,
        orderDirection: 'desc',
        orderBy: 'timestamp',
    }, {
        id: true,
        timestamp: true,
        account: true,
        abstractAccount: true,
        accountType: true,
        margin: true,
        size: true,
        asset: true,
        marketKey: true,
        price: true,
        positionId: true,
        positionSize: true,
        positionClosed: true,
        pnl: true,
        feesPaid: true,
        keeperFeesPaid: true,
        orderType: true,
        fundingAccrued: true,
        trackingCode: true,
    });
};
export const queryPositionHistory = (sdk, account, accountType) => {
    const accountQuery = accountType === 'eoa' ? { account: account } : { abstractAccount: account };
    return getFuturesPositions(sdk.futures.futuresGqlEndpoint, {
        where: accountQuery,
        first: 99999,
        orderBy: 'openTimestamp',
        orderDirection: 'desc',
    }, {
        id: true,
        lastTxHash: true,
        openTimestamp: true,
        closeTimestamp: true,
        timestamp: true,
        market: true,
        marketKey: true,
        asset: true,
        account: true,
        abstractAccount: true,
        accountType: true,
        isOpen: true,
        isLiquidated: true,
        trades: true,
        totalVolume: true,
        size: true,
        initialMargin: true,
        margin: true,
        pnl: true,
        feesPaid: true,
        netFunding: true,
        pnlWithFeesPaid: true,
        netTransfers: true,
        totalDeposits: true,
        fundingIndex: true,
        entryPrice: true,
        avgEntryPrice: true,
        lastPrice: true,
        exitPrice: true,
    });
};
export const queryIsolatedMarginTransfers = async (sdk, account) => {
    const response = await request(sdk.futures.futuresGqlEndpoint, ISOLATED_MARGIN_FRAGMENT, {
        walletAddress: account,
    });
    return response ? mapMarginTransfers(response.futuresMarginTransfers) : [];
};
export const querySmartMarginAccountTransfers = async (sdk, account) => {
    const response = await request(sdk.futures.futuresGqlEndpoint, SMART_MARGIN_FRAGMENT, {
        walletAddress: account,
    });
    return response ? mapSmartMarginTransfers(response.smartMarginAccountTransfers) : [];
};
export const queryFuturesTrades = (sdk, marketKey, minTs, maxTs) => {
    return getFuturesTrades(sdk.futures.futuresGqlEndpoint, {
        first: DEFAULT_NUMBER_OF_TRADES,
        where: {
            marketKey: toHex(marketKey, { size: 32 }),
            timestamp_gt: minTs,
            timestamp_lt: maxTs,
        },
        orderDirection: 'desc',
        orderBy: 'timestamp',
    }, {
        id: true,
        timestamp: true,
        account: true,
        abstractAccount: true,
        accountType: true,
        margin: true,
        size: true,
        marketKey: true,
        asset: true,
        price: true,
        positionId: true,
        positionSize: true,
        positionClosed: true,
        pnl: true,
        feesPaid: true,
        keeperFeesPaid: true,
        orderType: true,
        fundingAccrued: true,
        trackingCode: true,
    });
};
export const queryFundingRateHistory = async (sdk, marketAsset, minTimestamp, period = 'Hourly') => {
    const response = await request(sdk.futures.futuresGqlEndpoint, gql `
			query fundingRateUpdate(
				$marketAsset: Bytes!
				$minTimestamp: BigInt!
				$period: FundingRatePeriodType!
			) {
				fundingRatePeriods(
					where: { asset: $marketAsset, timestamp_gt: $minTimestamp, period: $period }
					first: 2000
				) {
					timestamp
					fundingRate
				}
			}
		`, { marketAsset: toHex(marketAsset, { size: 32 }), minTimestamp, period });
    return response.fundingRatePeriods.map((x) => ({
        timestamp: Number(x.timestamp) * 1000,
        fundingRate: Number(x.fundingRate),
    }));
};
export const queryDelegatesForAccount = async (sdk, walletAddress) => {
    const response = await request(sdk.futures.futuresGqlEndpoint, gql `
			query Delegates($walletAddress: String!) {
				delegatedAccounts(first: 1000, where: { caller: $walletAddress }) {
					delegate
				}
			}
		`, { walletAddress });
    const delegates = (await response.delegatedAccounts.map((entry) => ({
        delegate: entry.delegate,
    })));
    return delegates;
};
export const querySubAccountsForAccount = async (sdk, walletAddress) => {
    const response = await request(sdk.futures.futuresGqlEndpoint, gql `
			query SubAccounts($walletAddress: String!) {
				delegatedAccounts(first: 1000, where: { delegate: $walletAddress }) {
					caller
				}
			}
		`, { walletAddress });
    const subAccounts = (await response.delegatedAccounts.map((entry) => ({
        caller: entry.caller,
    })));
    return subAccounts;
};
export const queryOrders = async (sdk, account) => {
    return request(sdk.futures.futuresGqlEndpoint, gql `
			query FuturesOrders($account: String!) {
				futuresOrders(where: { account: $account }, orderBy: "timestamp", orderDirection: "desc") {
					id
					size
					marketKey
					account
					abstractAccount
					orderId
					targetPrice
					marginDelta
					timestamp
					orderType
					status
					keeper
					txnHash
				}
			}
		`, { account });
};
